const textEncoder = new TextEncoder();

type GlobalSubtleCrypto = typeof globalThis.crypto.subtle;
export type WebCryptoKey = Parameters<GlobalSubtleCrypto['encrypt']>[1];

function subtleCrypto(): GlobalSubtleCrypto {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) throw new Error('Web Crypto API is unavailable');
  return subtle;
}

type CryptoBufferSource = Parameters<GlobalSubtleCrypto['digest']>[1];

function asBufferSource(value: Uint8Array): CryptoBufferSource {
  return new Uint8Array(value);
}

export function concatBytes(...parts: readonly Uint8Array[]): Uint8Array {
  const result = new Uint8Array(parts.reduce((total, part) => total + part.byteLength, 0));
  let offset = 0;
  for (const part of parts) {
    result.set(part, offset);
    offset += part.byteLength;
  }
  return result;
}

export function utf8(value: string): Uint8Array {
  return textEncoder.encode(value);
}

export function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
}

export function hexToBytes(value: string): Uint8Array {
  if (!/^(?:[0-9a-f]{2})*$/iu.test(value)) throw new Error('Invalid hexadecimal input');
  const bytes = new Uint8Array(value.length / 2);
  for (let index = 0; index < bytes.length; index += 1) {
    bytes[index] = Number.parseInt(value.slice(index * 2, index * 2 + 2), 16);
  }
  return bytes;
}

export async function sha256(input: Uint8Array): Promise<Uint8Array> {
  return new Uint8Array(await subtleCrypto().digest('SHA-256', asBufferSource(input)));
}

export async function hmacSha256(keyBytes: Uint8Array, input: Uint8Array): Promise<Uint8Array> {
  const key = await subtleCrypto().importKey(
    'raw',
    asBufferSource(keyBytes),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  return new Uint8Array(await subtleCrypto().sign('HMAC', key, asBufferSource(input)));
}

export async function hkdfSha256(
  inputKeyMaterial: Uint8Array,
  salt: Uint8Array,
  info: Uint8Array,
  length: number,
): Promise<Uint8Array> {
  if (!Number.isInteger(length) || length < 1 || length > 255 * 32) {
    throw new Error('HKDF output length is outside the supported range');
  }
  const key = await subtleCrypto().importKey(
    'raw',
    asBufferSource(inputKeyMaterial),
    'HKDF',
    false,
    ['deriveBits'],
  );
  const bits = await subtleCrypto().deriveBits(
    { name: 'HKDF', hash: 'SHA-256', salt: asBufferSource(salt), info: asBufferSource(info) },
    key,
    length * 8,
  );
  return new Uint8Array(bits);
}

export type SessionDirection = 'browser-to-phone' | 'phone-to-browser';
export type SessionChannel = 'control' | 'file' | 'presence';

export interface SessionChannelMaterial {
  key: WebCryptoKey;
  noncePrefix: Uint8Array;
}

/** Derive the 32-byte application secret from the browser key and phone raw P-256 key. */
export async function deriveBrowserSharedSecret(
  browserPrivateKey: WebCryptoKey,
  phonePublicKey: Uint8Array,
): Promise<Uint8Array> {
  if (phonePublicKey.byteLength !== 65 || phonePublicKey[0] !== 4) {
    throw new Error('INVALID_PHONE_PUBLIC_KEY');
  }
  const publicKey = await subtleCrypto().importKey(
    'raw',
    asBufferSource(phonePublicKey),
    { name: 'ECDH', namedCurve: 'P-256' },
    false,
    [],
  );
  return new Uint8Array(
    await subtleCrypto().deriveBits({ name: 'ECDH', public: publicKey }, browserPrivateKey, 256),
  );
}

/** Derive the protocol-defined key and four-byte nonce prefix for one channel. */
export async function deriveSessionChannelMaterial(
  sharedSecret: Uint8Array,
  offerHash: Uint8Array,
  phonePublicKey: Uint8Array,
  direction: SessionDirection,
  channel: SessionChannel,
): Promise<SessionChannelMaterial> {
  const salt = await sha256(concatBytes(utf8('mlo/pairing/v1'), offerHash, phonePublicKey));
  const prefix = `mlo/session/v1/${direction}/${channel}`;
  const keyBytes = await hkdfSha256(sharedSecret, salt, utf8(`${prefix}/key`), 32);
  const noncePrefix = await hkdfSha256(sharedSecret, salt, utf8(`${prefix}/nonce`), 4);
  const key = await subtleCrypto().importKey(
    'raw',
    asBufferSource(keyBytes),
    { name: 'AES-GCM' },
    false,
    ['encrypt', 'decrypt'],
  );
  return { key, noncePrefix };
}

/** Six-digit authenticated pairing string from the v1 protocol contract. */
export async function deriveSessionVerificationCode(
  sharedSecret: Uint8Array,
  offerHash: Uint8Array,
  phonePublicKey: Uint8Array,
): Promise<string> {
  if (
    sharedSecret.byteLength !== 32 ||
    offerHash.byteLength !== 32 ||
    phonePublicKey.byteLength !== 65 ||
    phonePublicKey[0] !== 4
  ) {
    throw new Error('INVALID_SESSION_KEY_MATERIAL');
  }
  const salt = await sha256(concatBytes(utf8('mlo/pairing/v1'), offerHash, phonePublicKey));
  const verificationKey = await hkdfSha256(
    sharedSecret,
    salt,
    utf8('mlo/session/v1/verification'),
    32,
  );
  const mac = await hmacSha256(verificationKey, concatBytes(offerHash, phonePublicKey));
  const offset = (mac[mac.byteLength - 1] ?? 0) & 0x0f;
  const value =
    ((((mac[offset] ?? 0) & 0x7f) << 24) |
      (((mac[offset + 1] ?? 0) & 0xff) << 16) |
      (((mac[offset + 2] ?? 0) & 0xff) << 8) |
      ((mac[offset + 3] ?? 0) & 0xff)) %
    1_000_000;
  return value.toString().padStart(6, '0');
}
